import React from 'react'
import { Col, Row } from 'reactstrap'
import { InternalOperationProduct, InternalOperationRequest, OrderEvent, OrderEvents } from '../../api/order'
import { Product, ProductRequest } from '../../api/product'
import { ProductionOrder, ProductionOrderProduct } from '../../api/productionOrder'
import notesIcon from '../../assets/img/flags/notes.svg'
import resetIcon from '../../assets/img/flags/reset.svg'
import { BadgeSetting, EditorDescription, TogglePanel } from '../../models'
import routes from '../../route/routes'
import { OrderCampaignInfo, OrderDetailsServiceInstance as Service } from '../../services/OrderDetailsService'
import { highlightText } from '../../utils'
import { ReuploadForm } from '../forms/ReuploadForm'
import ProductOptionsPanel from '../panels/ProductOptionsPanel'
import SidePanel from '../panels/SidePanel'
import WidgetPanel from '../panels/WidgetPanel'
import ActionLink, { ActionLinkSettings } from './ActionLink'
import ActionList from './ActionList'
import Badge from './Badge'
import CampaignIcon from './CampaignIcon'
import EditorIcon from './EditorIcon'
import Flag from './Flag'
import Grid, { GridColumn } from './Grid'
import LazyContainer from './LazyContainer'
import moment from 'moment'

export interface ProductionOrderProductCardProps extends React.Props<any> {
  product: Product
  productionOrder?: ProductionOrder
  productionOrderProduct?: ProductionOrderProduct
  productEvents: OrderEvent[]
  vendor: string
  actions: ActionLinkSettings[]
  previewSrc: string
  optionsPanel?: TogglePanel
  reuploadPanel?: TogglePanel
  cancelReuploadPanel?: TogglePanel
  widgetPanel?: TogglePanel
  parentStatus: BadgeSetting
  onReuploadSubmit?(x: InternalOperationRequest): void
  onReuploadCancel?(x: ProductRequest): void
  highlighted: boolean
  notesCount: number
  widgetPreviewUrl?: string
  resetCounter: number
  onCreateWidgetClick?(): void
  onDeleteWidgetClick?(): void
  onRecreateWidgetClick?(): void
  onDelayLinkClick?(): void
}

/* Visually almost the same but not identical as ProductCard.
  Compared to ProductCard, some elements are removed and some repositioned. */
export default class ProductionOrderProductCard extends React.Component<ProductionOrderProductCardProps> {
  constructor(props: ProductionOrderProductCardProps) {
    super(props)

    this.cancelReuploadSubmit = this.cancelReuploadSubmit.bind(this)
    this.renderNotificationsIcons = this.renderNotificationsIcons.bind(this)
  }

  cancelReuploadSubmit() {
    if (this.props.onReuploadCancel) {
      this.props.onReuploadCancel({
        vendor: this.props.vendor,
        productCode: this.props.product.id,
      })
    }
  }

  render() {
    return (
      <React.Fragment key={this.props.product.id}>
        <Row className="mt-2 ml-0 mr-0 w-100">
          <div className="bo-container d-flex">
            <Col sm="12" md="4" lg="1" className="pl-0">
              <img src={this.props.previewSrc} width="100%" alt="preview" />
            </Col>
            <Col sm="12" md="8" lg="11" style={{ paddingLeft: '0.5rem' }}>
              {this.renderProductInfo()}
            </Col>
          </div>
        </Row>
        <Row className="mb-2 ml-0 mr-0 w-100">
          <ActionList actions={this.props.actions} />
          {this.props.optionsPanel && (
            <ProductOptionsPanel
              {...this.props.optionsPanel}
              product={this.props.product}
            />
          )}
          {this.props.reuploadPanel && this.props.productionOrder && this.props.onReuploadSubmit && (
            <SidePanel {...this.props.reuploadPanel}>
              <LazyContainer isLoading={this.props.reuploadPanel.isLoading}>
                <ReuploadForm
                  form={`${this.props.product.id}_reupload`}
                  initialValues={{
                    products: [],
                    productionOrders: [
                      {
                        productionOrderId: this.props.productionOrder.id,
                        products: [
                          {
                            id: this.props.product.id,
                            quantity: this.props.product.quantity,
                            value: this.props.product.quantity,
                          } as InternalOperationProduct,
                        ],
                      },
                    ],
                  }}
                  onSubmit={this.props.onReuploadSubmit}
                />
              </LazyContainer>
            </SidePanel>
          )}
          {this.props.cancelReuploadPanel && (
            <SidePanel {...this.props.cancelReuploadPanel}>
              <LazyContainer isLoading={this.props.cancelReuploadPanel.isLoading}>
                <button onClick={this.cancelReuploadSubmit}>Submit</button>
              </LazyContainer>
            </SidePanel>
          )}
          {this.renderWidgetPanel()}
        </Row>
      </React.Fragment>
    )
  }

  renderWidgetPanel() {
    if (
      !this.props.widgetPanel ||
      !this.props.onCreateWidgetClick ||
      !this.props.onDeleteWidgetClick ||
      !this.props.onRecreateWidgetClick
    ) {
      return
    }
    return (
      <WidgetPanel
        {...this.props.widgetPanel}
        product={this.props.product}
        widgetPreviewUrl={this.props.widgetPreviewUrl}
        onCreateClick={this.props.onCreateWidgetClick}
        onDeleteClick={this.props.onDeleteWidgetClick}
        onRecreateClick={this.props.onRecreateWidgetClick}
      />
    )
  }

  renderNotificationsIcons(product: any) {
    return (
      <>
        {product.promisedCampaign && !product.promisedCampaign.unpromised && (
          <CampaignIcon campaign={product.promisedCampaign} productId={product.id} />
        )}
        {!!this.props.notesCount && <Flag icon={notesIcon} title={`Notes (${this.props.notesCount})`} />}
        {product.reuploadState && <Flag icon={product.reuploadState.image} title={product.reuploadState.title} />}
        {!!this.props.resetCounter && <Flag icon={resetIcon} title={`Reset product (x${this.props.resetCounter})`} />}
      </>
    )
  }

  renderProductInfo() {
    const productNameFunc = (p: any) => {
      const getNameFunc = function () {
        if (!p.name) {
          return <i>Unknown product</i>
        }

        return `${p.name} ${p.isLayflat ? ' | layflat' : ''}`
      }

      const [reuploadedEventOld] = this.props.productEvents.filter((e) => e.event === OrderEvents.ReuploadedOriginal)
      const [reuploadedEventNew] = this.props.productEvents.filter((e) => e.event === OrderEvents.ReuploadedNew)

      return (
        <div>
          <div>{getNameFunc()}</div>
          {reuploadedEventOld && (
            <div className="mt-2">
              <ActionLink
                className="attention normal-font"
                route={routes.ORDERS_VIEW({ orderId: reuploadedEventOld.comment })}
                text={`Reuploaded as ${reuploadedEventOld.comment}`}
              />
            </div>
          )}
          {reuploadedEventNew && (
            <div className="mt-2">
              <ActionLink
                className="attention normal-font"
                route={routes.ORDERS_VIEW({ orderId: reuploadedEventNew.comment })}
                text={`Reupload of ${reuploadedEventNew.comment}`}
              />
            </div>
          )}
        </div>
      )
    }

    const columnsLeft: GridColumn<any>[] = [
      { title: 'Product number', value: (p) => highlightText(p.code, this.props.highlighted) },
      { title: this.props.product.title ? `“${this.props.product.title}”` : '', value: productNameFunc },
      {
        title: 'Editor',
        tdClass: 'text-center',
        value: (p) => {
          const editor: EditorDescription = p.editor
          const elementId = `editor_tooltip_${p.id}`
          return <EditorIcon description={editor} elementId={elementId} />
        },
      },
    ]

    const infoLeft = {
      name: Service.resolveProductDescriptionByType(this.props.product.productId),
      isLayflat: Service.isProductLayflat(this.props.product),
      code: this.props.productionOrderProduct?.id,
      editor: Service.resolveEditor(this.props.product.editor),
    }

    const columnsRight: GridColumn<any>[] = [
      {
        title: 'Notifications',
        tdClass: 'text-center d-flex justify-content-between',
        value: this.renderNotificationsIcons,
      },
      {
        title: `Status & quantity (${this.props.productionOrderProduct !== undefined
          ? this.props.productionOrderProduct.quantity
          : this.props.product.quantity
          })`,
        value: (p) => (
          <div>
            {p.statuses &&
              p.statuses.map((s: BadgeSetting, i: number) => (
                <div key={i}>
                  <Badge setting={s} className="fixed-size" />
                </div>
              ))}
            {p.isDelayed && (
              <ActionLink className="attention normal-font" onClick={this.props.onDelayLinkClick} text="Shipment is delayed" />
            )}
          </div>
        ),
      },
    ]

    //After 2024, a PromisedCampaign is present on the ProductionOrder. Before that time, a promise/unpromise/brokenpromise event based system was in place
    const resolvePromisedCampaign = (productionOrder: ProductionOrder | undefined, productEvents: OrderEvent[]): OrderCampaignInfo | undefined => {
      if (productionOrder?.createdAt && moment(productionOrder?.createdAt).year() >= 2025) {
        if (productionOrder.promisedCampaign && productionOrder.promisedCampaign !== '') {
          return { campaignName: productionOrder.promisedCampaign, unpromised: false, isBrokenPromise: false }
        } else {
          return undefined
        }
      } else {
        return Service.resolvePromisedCampaign(productEvents)
      }
    }

    const infoRight = {
      id: this.props.product.id,
      statuses: Service.resolveProductionOrderProductStatuses(this.props.productionOrderProduct!, this.props.productionOrder?.id, this.props.productEvents, this.props.parentStatus),
      promisedCampaign: resolvePromisedCampaign(this.props.productionOrder, this.props.productEvents),
      isDelayed: Service.isDelayed(this.props.productEvents),
      reuploadState: Service.resolveReuploadState(this.props.productEvents),
    }

    return (
      <div className="d-flex justify-content-between">
        <div>
          <Grid columns={columnsLeft} data={[infoLeft]} className="embedded" />
        </div>
        <div>
          <Grid
            columns={columnsRight}
            data={[infoRight]}
            className="all-eq last-column-status embedded"
            disableCopyOnClick={true}
          />
        </div>
      </div>
    )
  }
}
